import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type ComfortInput,
  type ComfortResult,
  type ComfortRuimteInput,
  type RuimtePositie,
  type UserSegment,
  type TypeDak,
  type TypeGlas,
  type TypeWoning,
  type OrientatieRamen,
  type Zonwering,
  type IsolatieProfiel,
  type RenovatieScenario,
  type Luchtdichtheid,
  type MeterkastStatus,
  type WaterWarmtepompDichtbij,
  type BestaandeVerwarming,
  type PandGebruik,
  type KlusOmvang,
  type WarmteVoorkeur,
  computeComfortEngine,
  applyClimateYearlyToResult,
  defaultComfortInput,
  loadComfortState,
  persistComfortState,
  validateComfortInput,
  COMFORT_ENGINE_VERSION,
  USER_SEGMENT_LABELS,
  RUIMTE_POSITIE_LABELS,
} from '../lib/comfortCalc';
import {
  fetchComforttoolConfig,
  fetchClimateForPostcode,
  fetchComfortReport,
  fetchComforttoolHourlyThermal,
  startComforttoolCheckout,
  type ComforttoolConfig,
  type HourlyThermalApiSuccess,
  type ThermalInertiaOption,
} from '../lib/api';
import { normalizeComfortInput, type ConstructieTabelModus } from '@comforttool/comfortcore';
import { AnimatedBar } from '../components/AnimatedBar';
import { HelpTip } from '../components/HelpTip';
import { Seo } from '../components/Seo';
import { SimpleMarkdown } from '../components/SimpleMarkdown';
import { ResultPurchaseSection } from '../components/ResultPurchaseSection';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  ClipboardCheck,
  FileDown,
  Sparkles,
  SunMedium,
  Thermometer,
  UserCircle,
  Zap,
} from 'lucide-react';
import { downloadComfortSummaryCsv } from '../lib/comfortSummaryCsv';
import type { BundleTier, CheckoutAddonId } from '../lib/pricing';
import {
  indicativeBundlePriceLine,
  indicativeBundlePriceLineForInput,
  indicativePricingParagraph,
  prijsSnapshotVoorInvoer,
} from '../lib/pricing';
import {
  gebouwOptiesVoorSegment,
  getScanContext,
  pandGebruikKeuzes,
  syncTypeWoningVoorSegment,
  wizardStepTopLabels,
} from '../lib/scanContext';

const INERTIA_OPTIONS: { id: ThermalInertiaOption; label: string }[] = [
  { id: 'licht', label: 'Licht (weinig massa in muren/vloer)' },
  { id: 'normaal', label: 'Normaal' },
  { id: 'zwaar', label: 'Zwaar (veel massa — bv. beton)' },
];

/** Gebruikersteksten — geen verwijzing naar normtabellen. */
const SCHIL_VERGELIJK_MODI: { value: ConstructieTabelModus; label: string }[] = [
  { value: 'standaard_bouwjaar', label: 'Standaard op basis van bouwjaar' },
  { value: 'issoproxy_streng', label: 'Strakker geïsoleerd (gunstig scenario)' },
  { value: 'issoproxy_versoepeld', label: 'Meer warmteverlies (voorzichtig scenario)' },
  { value: 'renovatie_doel_armer', label: 'Alsof flink verbouwd naar laag verbruik' },
  { value: 'nieuwbouw_proxy', label: 'Alsof bijna nieuwbouw' },
];

const RESULT_STEP = 8;
const WIZARD_LAST_INDEX = 7;

const ADVIES_CAT_NL: Record<string, string> = {
  officieel: 'Regels & officiële trajecten',
  installatie: 'Installatie op locatie',
  warmtebron: 'Warmtebron',
  afgifte: 'Afgifte (vloer, radiatoren, …)',
  koeling: 'Koeling',
  combinatie: 'Combinaties',
  ventilatie: 'Ventilatie',
  overig: 'Overig',
};

function newRoomId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function HomeScan() {
  const { token: authToken } = useAuth();
  const navigate = useNavigate();
  const loaded = useMemo(() => loadComfortState(), []);
  const [step, setStep] = useState(1);
  const [input, setInput] = useState<ComfortInput>(() =>
    loaded?.input ? normalizeComfortInput(loaded.input) : defaultComfortInput()
  );
  const [result, setResult] = useState<ComfortResult | null>(() => loaded?.result ?? null);
  const [errors, setErrors] = useState<string[]>([]);
  const [apiConfig, setApiConfig] = useState<ComforttoolConfig | null>(null);
  const [payEmail, setPayEmail] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutAddons, setCheckoutAddons] = useState<CheckoutAddonId[]>([]);
  const [bundleTier, setBundleTier] = useState<BundleTier>('plus');
  const [reportBrandingLine, setReportBrandingLine] = useState('');
  const [climateLoading, setClimateLoading] = useState(false);
  const [reportMarkdown, setReportMarkdown] = useState<string | null>(null);
  const [reportServerDisclaimer, setReportServerDisclaimer] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [hourlySim, setHourlySim] = useState<HourlyThermalApiSuccess | null>(null);
  const [hourlySimLoading, setHourlySimLoading] = useState(false);
  const [hourlySimError, setHourlySimError] = useState<string | null>(null);
  const [thermalInertia, setThermalInertia] = useState<ThermalInertiaOption>('normaal');

  useEffect(() => {
    setHourlySim(null);
  }, [thermalInertia]);

  useEffect(() => {
    fetchComforttoolConfig()
      .then(setApiConfig)
      .catch(() => setApiConfig({}));
  }, []);

  const woonCheckoutOk =
    apiConfig?.checkout_available === true && apiConfig?.products?.woon?.available === true;
  const reportAvailable = apiConfig?.report_available === true;

  const scanCtx = useMemo(
    () => getScanContext(input.type_woning, input.user_segment),
    [input.type_woning, input.user_segment]
  );
  const stepPills = useMemo(() => wizardStepTopLabels(scanCtx), [scanCtx]);
  const prijsSnapshot = useMemo(
    () =>
      prijsSnapshotVoorInvoer({
        user_segment: input.user_segment,
        vloeroppervlakte: input.vloeroppervlakte,
        type_woning: input.type_woning,
        bundleTier,
      }),
    [input.user_segment, input.vloeroppervlakte, input.type_woning, bundleTier]
  );
  function upd<K extends keyof ComfortInput>(k: K, v: ComfortInput[K]) {
    setInput((p) => {
      let next = { ...p, [k]: v } as ComfortInput;
      if (k === 'vloeroppervlakte' && typeof v === 'number') {
        if (p.ruimtes.length === 1 && p.ruimtes[0].id === 'heel-woning') {
          next = {
            ...next,
            ruimtes: [{ ...p.ruimtes[0], vloeroppervlakte_m2: v }],
          };
        }
      }
      return next;
    });
  }

  function addRoom() {
    setInput((p) => {
      if (p.ruimtes.length === 1 && p.ruimtes[0].id === 'heel-woning') {
        const t = Math.max(p.vloeroppervlakte, 1);
        const a = Math.round((t / 2) * 10) / 10;
        const b = Math.round((t - a) * 10) / 10;
        return {
          ...p,
          ruimtes: [
            {
              id: newRoomId(),
              naam: 'Woonkamer',
              vloeroppervlakte_m2: a,
              positie: 'buiten',
              verdieping: 1,
              buitenwand_m2: 0,
              ramen_m2: 0,
              wand_naar_ongverwarmd_m2: 0,
            },
            {
              id: newRoomId(),
              naam: 'Slaapkamer',
              vloeroppervlakte_m2: b,
              positie: 'half_buiten',
              verdieping: 2,
              buitenwand_m2: 0,
              ramen_m2: 0,
              wand_naar_ongverwarmd_m2: 0,
            },
          ],
        };
      }
      return {
        ...p,
        ruimtes: [
          ...p.ruimtes,
          {
            id: newRoomId(),
            naam: 'Nieuwe ruimte',
            vloeroppervlakte_m2: 12,
            positie: 'half_buiten',
            verdieping: 1,
            buitenwand_m2: 0,
            ramen_m2: 0,
            wand_naar_ongverwarmd_m2: 0,
          },
        ],
      };
    });
  }

  function removeRoom(id: string) {
    setInput((p) => {
      if (p.ruimtes.length <= 1) return p;
      return { ...p, ruimtes: p.ruimtes.filter((r) => r.id !== id) };
    });
  }

  function patchRoom(id: string, patch: Partial<ComfortRuimteInput>) {
    setInput((p) => ({
      ...p,
      ruimtes: p.ruimtes.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  }

  function patchSituatie<K extends keyof ComfortInput['situatie']>(k: K, v: ComfortInput['situatie'][K]) {
    setInput((p) => ({ ...p, situatie: { ...p.situatie, [k]: v } }));
  }

  function patchWensen<K extends keyof ComfortInput['wensen']>(k: K, v: ComfortInput['wensen'][K]) {
    setInput((p) => ({ ...p, wensen: { ...p.wensen, [k]: v } }));
  }

  async function runEngine() {
    const ni = normalizeComfortInput(input);
    setInput(ni);
    const e = validateComfortInput(ni);
    setErrors(e);
    if (e.length) return;
    const base = computeComfortEngine(ni);
    setResult(base);
    persistComfortState(ni, base);
    setReportMarkdown(null);
    setReportError(null);
    setHourlySim(null);
    setHourlySimError(null);
    setStep(RESULT_STEP);
    setClimateLoading(true);
    try {
      const cr = await fetchClimateForPostcode(ni.postcode);
      if (cr.ok) {
        const merged = applyClimateYearlyToResult(base, cr.climate, ni);
        setResult(merged);
        persistComfortState(ni, merged);
      }
    } finally {
      setClimateLoading(false);
    }
  }

  async function loadHourlyThermal() {
    setHourlySimError(null);
    setHourlySimLoading(true);
    try {
      const ni = normalizeComfortInput(input);
      const r = await fetchComforttoolHourlyThermal(ni, { thermalInertia });
      if (r.ok) {
        setHourlySim(r);
      } else {
        setHourlySimError(r.errors.join(' ') || r.error || 'Uurlijkse simulatie mislukt.');
      }
    } finally {
      setHourlySimLoading(false);
    }
  }

  async function loadFullReport() {
    setReportError(null);
    setReportServerDisclaimer(null);
    setReportLoading(true);
    try {
      const ni = normalizeComfortInput(input);
      const r = await fetchComfortReport(ni, {
        bundleTier,
        brandingLine: bundleTier === 'plus' ? reportBrandingLine : undefined,
      });
      if (r.ok) {
        setReportMarkdown(r.markdown);
        setReportServerDisclaimer(r.disclaimer ?? null);
      } else {
        setReportError(r.message ?? r.error ?? 'Rapport mislukt.');
      }
    } finally {
      setReportLoading(false);
    }
  }

  async function goToStripe() {
    setCheckoutError(null);
    if (prijsSnapshot.requiresQuote) {
      setCheckoutError(
        'Voor dit oppervlak kun je niet direct online afrekenen — vraag een offerte via het contactformulier.'
      );
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payEmail.trim())) {
      setCheckoutError('Vul een geldig e-mailadres in.');
      return;
    }
    if (!result) return;
    setCheckoutLoading(true);
    try {
      const res = await startComforttoolCheckout(
        {
          customer_email: payEmail.trim(),
          input: normalizeComfortInput(input),
          bundle_tier: bundleTier,
          addons: checkoutAddons,
          success_url: `${window.location.origin}/bedankt?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/scan`,
        },
        authToken
      );
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      setCheckoutError(res.message ?? res.error ?? 'De betaling kon niet worden gestart. Probeer het opnieuw of neem contact op.');
    } finally {
      setCheckoutLoading(false);
    }
  }

  const currentYear = new Date().getFullYear();
  const bouwjaarOptions = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);

  const maxScenarioPiek = result
    ? Math.max(...result.scenarios.map((s) => s.pieklastKW), 0.1)
    : 1;

  return (
    <>
      <Seo
        path="/scan"
        title="Warmte- en koelscan"
        description="ComfortTool™ scan: vul gebouw en ruimtes in — indicatieve warmte- en koellast (kW), jaarschatting, optioneel uurlijks weer en Comfort Basis of Plus rapport."
        keywords="ComfortTool scan, warmtescan, koelscan, vermogen, koellast, warmtelast, online"
      />
      <div className="mx-auto max-w-5xl px-4 py-6 sm:py-10">
        {result ? (
          <p className="mb-6 text-center text-xs text-ct-muted">
            <span className="rounded-full bg-ct-elevated px-3 py-1">Uitslag beschikbaar</span>
          </p>
        ) : null}
        {step < RESULT_STEP && (
          <>
            <div className="mb-8 flex flex-wrap gap-2">
              {stepPills.map((label, i) => {
                const pillStep = i + 1;
                return (
                  <button
                    key={`${label}-${i}`}
                    type="button"
                    onClick={() => pillStep < step && setStep(pillStep)}
                    disabled={pillStep > step}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-all duration-300 ${
                      pillStep === step
                        ? 'scale-[1.02] bg-ct-accent text-white shadow-md shadow-ct-accent/25'
                        : pillStep < step
                          ? 'bg-ct-elevated text-ct-muted hover:text-ct-text'
                          : 'bg-ct-surface text-ct-muted/50'
                    }`}
                  >
                    {pillStep}. {label}
                  </button>
                );
              })}
            </div>

            {step >= 1 && step <= WIZARD_LAST_INDEX && (
              <p className="mb-4 rounded-xl border border-ct-border/80 bg-ct-elevated/40 px-4 py-3 text-xs leading-relaxed text-ct-muted">
                <strong className="text-ct-text">Invullen:</strong> * = verplicht om het eindresultaat te kunnen maken.
                Oppervlaktes en hoogtes moeten groter dan 0 zijn, tenzij er bij het veld staat dat 0 mag (bijv. m² ramen
                per kamer = “laat ComfortTool verdelen”). Bij keuzes in de scan telt elke optie, ook “weet ik niet”.
              </p>
            )}

            {errors.length > 0 && (
              <div className="mb-6 rounded-xl ct-caution-panel px-4 py-3 text-sm ct-caution-text" role="alert">
                <ul className="list-inside list-disc">
                  {errors.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            )}

            {step === 1 && (
              <StepCard
                title={
                  <>
                    <UserCircle className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>Voor wie vul je dit in?</span>
                  </>
                }
              >
                <p className="text-sm leading-relaxed text-ct-muted">
                  Dezelfde berekening voor iedereen — we passen alleen de toon van het schriftelijke advies aan (particulier,
                  installateur, bedrijf, gemeente, corporatie, VvE, enz.).
                </p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {(Object.keys(USER_SEGMENT_LABELS) as UserSegment[]).map((id) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() =>
                        setInput((p) => ({
                          ...p,
                          user_segment: id,
                          type_woning: syncTypeWoningVoorSegment(p.type_woning, id),
                        }))
                      }
                      className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                        input.user_segment === id
                          ? 'border-ct-accent bg-ct-accent/15 text-ct-text'
                          : 'border-ct-border bg-ct-elevated text-ct-muted hover:border-ct-accent/40'
                      }`}
                    >
                      <span className="font-semibold text-ct-text">{USER_SEGMENT_LABELS[id]}</span>
                    </button>
                  ))}
                </div>
                <StepNav
                  onBack={() => navigate('/')}
                  onNext={() => setStep(2)}
                  stepIndex={0}
                  totalSteps={7}
                  nextStepTitle={stepPills[1]}
                />
              </StepCard>
            )}

            {step === 2 && (
              <StepCard
                title={
                  <>
                    <Building2 className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>Je gebouw in hoofdlijnen</span>
                  </>
                }
              >
                <p className="text-sm text-ct-muted">
                  De lijst met <strong className="text-ct-text">soort gebouw</strong> past zich aan op wie je net koos —
                  woning, kantoor, VvE, enz.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Vloeroppervlakte (m²) *"
                    tip="Hele vloer die je meerekent, bij elkaar opgeteld."
                  >
                    <Num value={input.vloeroppervlakte} onChange={(v) => upd('vloeroppervlakte', v)} />
                  </Field>
                  <Field label="Plafondhoogte (m) *" tip="Gemiddelde hoogte; per kamer kun je later afwijken.">
                    <Num value={input.plafondhoogte} step={0.1} onChange={(v) => upd('plafondhoogte', v)} />
                  </Field>
                  <Field label="Aantal verdiepingen *" tip="Minstens 1. Telt mee voor de schatting van de schil.">
                    <Num value={input.bouwlagen} min={1} onChange={(v) => upd('bouwlagen', v)} />
                  </Field>
                  <Field label="Bouwjaar *">
                    <select
                      value={input.bouwjaar}
                      onChange={(e) => upd('bouwjaar', Number(e.target.value))}
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      {bouwjaarOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={scanCtx.gebouwVormLabel} tip="Kies wat het dichtst in de buurt komt — de scan gebruikt dit voor de buitenschil.">
                    <select
                      value={input.type_woning}
                      onChange={(e) => upd('type_woning', e.target.value as TypeWoning)}
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      {gebouwOptiesVoorSegment(input.user_segment).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Dakvorm *" tip="Plat dak telt in de scan iets zwaarder mee voor de schil.">
                    <Select
                      value={input.type_dak}
                      options={
                        ['Plat dak', 'Zadeldak', 'Schilddak', 'Lessenaarsdak', 'Onbekend'] as TypeDak[]
                      }
                      onChange={(v) => upd('type_dak', v)}
                    />
                  </Field>
                </div>
                <StepNav
                  onBack={() => setStep(1)}
                  onNext={() => setStep(3)}
                  stepIndex={1}
                  totalSteps={7}
                  prevStepTitle={stepPills[0]}
                  nextStepTitle={stepPills[2]}
                />
              </StepCard>
            )}

            {step === 3 && (
              <StepCard
                title={
                  <>
                    <Thermometer className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>Hoe woont het — en waar ongeveer?</span>
                  </>
                }
              >
                <p className="text-sm leading-relaxed text-ct-muted">
                  Hoe warm je het graag hebt, hoeveel mensen meetellen, en je postcode (alleen vier cijfers) voor het
                  weerbeeld in je streek. Geen huisnummer nodig.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Gewenste temperatuur binnen (°C) *"
                    tip="Waar je het in de winter ongeveer warm wilt hebben."
                  >
                    <Num value={input.temperatuur} step={0.5} onChange={(v) => upd('temperatuur', v)} />
                  </Field>
                  <Field label={scanCtx.personenLabel} tip={scanCtx.personenTip}>
                    <Num value={input.personen} min={1} onChange={(v) => upd('personen', v)} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field
                      label="Postcode (eerste vier cijfers) *"
                      tip="Alleen de cijfers vóór de letters. Daarmee koppelen we grofweg aan het weer in jouw streek."
                    >
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="postal-code"
                        maxLength={7}
                        placeholder="1234"
                        value={input.postcode}
                        onChange={(e) => upd('postcode', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text placeholder:text-ct-muted/50"
                      />
                    </Field>
                  </div>
                </div>
                <StepNav
                  onBack={() => setStep(2)}
                  onNext={() => setStep(4)}
                  stepIndex={2}
                  totalSteps={7}
                  prevStepTitle={stepPills[1]}
                  nextStepTitle={stepPills[3]}
                />
              </StepCard>
            )}

            {step === 4 && (
              <StepCard
                title={
                  <>
                    <SunMedium className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>{scanCtx.ruimtesStepTitel}</span>
                  </>
                }
              >
                <p className="text-sm leading-relaxed text-ct-muted">{scanCtx.ruimtesStepIntro}</p>
                <p className="mt-2 text-xs text-ct-muted">
                  Som van kamers:{' '}
                  <strong className="text-ct-text">
                    {input.ruimtes.reduce((s, r) => s + r.vloeroppervlakte_m2, 0).toFixed(1)} m²
                  </strong>{' '}
                  · totaal gebouw: <strong className="text-ct-text">{input.vloeroppervlakte} m²</strong>
                </p>

                <div className="mt-6 rounded-xl border border-ct-accent/30 bg-ct-accent/5 p-4">
                  <h3 className="text-sm font-bold text-ct-text">{scanCtx.standaardSchilTitel}</h3>
                  <p className="mt-1 text-xs text-ct-muted">{scanCtx.standaardSchilSub}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Type glas (gemiddeld) *"
                      tip="Kies wat het meeste in je gebouw lijkt. Per kamer kun je afwijken."
                    >
                      <Select
                        value={input.type_glas}
                        options={
                          ['Enkel glas', 'Dubbel glas', 'HR++ glas', 'Triple glas', 'Onbekend'] as TypeGlas[]
                        }
                        onChange={(v) => upd('type_glas', v)}
                      />
                    </Field>
                    <Field
                      label="Totaal raamoppervlak (m²) *"
                      tip="Alle ramen bij elkaar. We verdelen het over kamers tenzij je per kamer m² invult."
                    >
                      <Num value={input.raamoppervlakte} step={0.1} onChange={(v) => upd('raamoppervlakte', v)} />
                    </Field>
                    <Field
                      label="Kant waar de meeste zon op valt"
                      tip="Waar zitten je grootste ramen? Zuid telt in deze schatting het meest mee op een warme dag."
                    >
                      <Select
                        value={input.orientatie_ramen}
                        options={
                          ['Zuid', 'West', 'Oost', 'Noord', 'Meerdere richtingen'] as OrientatieRamen[]
                        }
                        onChange={(v) => upd('orientatie_ramen', v)}
                      />
                    </Field>
                    <Field label="Zonwering" tip="Zonnescherm, buitenlamellen of gordijnen die echt tegen zon werken.">
                      <Select
                        value={input.zonwering}
                        options={['Ja', 'Deels', 'Nee'] as Zonwering[]}
                        onChange={(v) => upd('zonwering', v)}
                      />
                    </Field>
                    <Field
                      label="Hoe goed zijn muren, dak en vloer geïsoleerd?"
                      tip="Niet het glas — de schil eromheen. ‘Geschat op bouwjaar’ laat ons meedenken."
                    >
                      <Select
                        value={input.isolatie_profiel}
                        options={
                          [
                            'Geschat op bouwjaar',
                            'Slecht',
                            'Matig',
                            'Goed',
                            'Zeer goed',
                          ] as IsolatieProfiel[]
                        }
                        onChange={(v) => upd('isolatie_profiel', v)}
                      />
                    </Field>
                    <Field
                      label="Verbouwplannen (strakkere isolatie)"
                      tip="Als je nog gaat isoleren, wordt de schatting gunstiger. ‘Diep’ = veel aanpakken."
                    >
                      <Select
                        value={input.renovatie_scenario}
                        options={['Geen', 'Basis', 'Diep'] as RenovatieScenario[]}
                        onChange={(v) => upd('renovatie_scenario', v)}
                      />
                    </Field>
                    <Field
                      label={scanCtx.utiliteit ? 'Hoe strak sluit het gebouw?' : 'Hoe strak sluit het huis?'}
                      tip="Hoe minder kieren, hoe minder warmte weglekt met de lucht. ‘Strak’ is bijna-energieneutraal niveau."
                    >
                      <Select
                        value={input.luchtdichtheid}
                        options={['Loos', 'Normaal', 'Strak'] as Luchtdichtheid[]}
                        onChange={(v) => upd('luchtdichtheid', v)}
                      />
                    </Field>
                    <Field
                      label="Scenario voor muren & dak (niet je energielabel)"
                      tip="Standaard volgt je bouwjaar. Andere opties zijn om te vergelijken (‘wat als’). Geen officiële constructietabel."
                    >
                      <select
                        value={input.constructie_tabel}
                        onChange={(e) => upd('constructie_tabel', e.target.value as ConstructieTabelModus)}
                        className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                      >
                        {SCHIL_VERGELIJK_MODI.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label="Extra verlies door koude plekken (%)"
                      tip="Naden, balkkoppen, aansluitingen: typisch 0–8%. Alleen verhogen als je weet dat het een issue is (max. 35%)."
                    >
                      <Num
                        value={Math.round(input.warmtebrug_toeslag_frac * 100)}
                        min={0}
                        max={35}
                        onChange={(v) =>
                          upd('warmtebrug_toeslag_frac', Math.min(35, Math.max(0, v)) / 100)
                        }
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  {input.ruimtes.map((r) => (
                    <div
                      key={r.id}
                      className="rounded-xl border border-ct-border bg-ct-elevated/40 p-4"
                    >
                      <div className="flex flex-wrap items-end gap-3">
                        <Field label="Naam kamer">
                          <input
                            type="text"
                            value={r.naam}
                            onChange={(e) => patchRoom(r.id, { naam: e.target.value })}
                            className="w-full min-w-[140px] rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
                          />
                        </Field>
                        <Field label="Verdieping" tip="1 = begane grond, 2 = eerste verdieping.">
                          <Num
                            value={r.verdieping ?? 1}
                            min={1}
                            onChange={(v) => patchRoom(r.id, { verdieping: Math.max(1, Math.floor(v || 1)) })}
                          />
                        </Field>
                        <Field label="Vloer m²">
                          <Num
                            value={r.vloeroppervlakte_m2}
                            step={0.1}
                            onChange={(v) => patchRoom(r.id, { vloeroppervlakte_m2: v })}
                          />
                        </Field>
                        <Field
                          label={scanCtx.utiliteit ? 'Ligging in het gebouw' : 'Ligging in het huis'}
                          tip="Een hoekkamer aan de straat verliest meer warmte dan een kamer middenin."
                        >
                          <select
                            value={r.positie}
                            onChange={(e) => patchRoom(r.id, { positie: e.target.value as RuimtePositie })}
                            className="w-full min-w-[200px] rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                          >
                            {(Object.keys(RUIMTE_POSITIE_LABELS) as RuimtePositie[]).map((pid) => (
                              <option key={pid} value={pid}>
                                {RUIMTE_POSITIE_LABELS[pid]}
                              </option>
                            ))}
                          </select>
                        </Field>
                        {input.ruimtes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRoom(r.id)}
                            className="mb-0.5 rounded-lg border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-400 hover:bg-red-500/10"
                          >
                            Verwijderen
                          </button>
                        )}
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Field label="Plafondhoogte (m)" tip="0 = zelfde hoogte als bij ‘gebouw’ hierboven.">
                          <Num
                            value={r.plafond_hoogte_m ?? 0}
                            step={0.01}
                            onChange={(v) =>
                              patchRoom(r.id, { plafond_hoogte_m: v > 0 ? v : undefined })
                            }
                          />
                          <p className="mt-1 text-[11px] text-ct-muted">
                            0 = zelfde als heel {scanCtx.pandWoord === 'huis' ? 'huis' : 'gebouw'}
                          </p>
                        </Field>
                        <Field label="Buitenwand m²" tip="Muur naar buiten. 0 = automatisch verdelen.">
                          <Num
                            value={r.buitenwand_m2 ?? 0}
                            step={0.1}
                            onChange={(v) => patchRoom(r.id, { buitenwand_m2: v })}
                          />
                          <p className="mt-1 text-[11px] text-ct-muted">0 = auto</p>
                        </Field>
                        <Field label="Ramen m² in deze kamer" tip="0 = verdeel totaal naar vloeroppervlak.">
                          <Num
                            value={r.ramen_m2 ?? 0}
                            step={0.1}
                            onChange={(v) => patchRoom(r.id, { ramen_m2: v })}
                          />
                          <p className="mt-1 text-[11px] text-ct-muted">0 = auto</p>
                        </Field>
                        <Field label="Muur naar koude ruimte m²" tip="Garage, onverwarmde hal, kelder.">
                          <Num
                            value={r.wand_naar_ongverwarmd_m2 ?? 0}
                            step={0.1}
                            onChange={(v) => patchRoom(r.id, { wand_naar_ongverwarmd_m2: v })}
                          />
                        </Field>
                      </div>
                      <div className="mt-4 border-t border-ct-border/60 pt-4">
                        <p className="text-xs font-semibold text-ct-muted">Alleen deze kamer (leeg = zelfde als hierboven)</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <Field label="Glas">
                            <select
                              value={r.type_glas ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRoom(r.id, { type_glas: v === '' ? undefined : (v as TypeGlas) });
                              }}
                              className="w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                            >
                              <option value="">Zelfde als standaard</option>
                              {(['Enkel glas', 'Dubbel glas', 'HR++ glas', 'Triple glas', 'Onbekend'] as TypeGlas[]).map(
                                (x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                )
                              )}
                            </select>
                          </Field>
                          <Field label="Zonkant ramen">
                            <select
                              value={r.orientatie_ramen ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRoom(r.id, {
                                  orientatie_ramen: v === '' ? undefined : (v as OrientatieRamen),
                                });
                              }}
                              className="w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                            >
                              <option value="">Zelfde als standaard</option>
                              {(['Zuid', 'West', 'Oost', 'Noord', 'Meerdere richtingen'] as OrientatieRamen[]).map(
                                (x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                )
                              )}
                            </select>
                          </Field>
                          <Field label="Zonwering">
                            <select
                              value={r.zonwering ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRoom(r.id, { zonwering: v === '' ? undefined : (v as Zonwering) });
                              }}
                              className="w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                            >
                              <option value="">Zelfde als standaard</option>
                              {(['Ja', 'Deels', 'Nee'] as Zonwering[]).map((x) => (
                                <option key={x} value={x}>
                                  {x}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Isolatie muren/dak">
                            <select
                              value={r.isolatie_profiel ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRoom(r.id, {
                                  isolatie_profiel: v === '' ? undefined : (v as IsolatieProfiel),
                                });
                              }}
                              className="w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                            >
                              <option value="">Zelfde als standaard</option>
                              {(
                                [
                                  'Geschat op bouwjaar',
                                  'Slecht',
                                  'Matig',
                                  'Goed',
                                  'Zeer goed',
                                ] as IsolatieProfiel[]
                              ).map((x) => (
                                <option key={x} value={x}>
                                  {x}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Verbouw / strakker maken">
                            <select
                              value={r.renovatie_scenario ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRoom(r.id, {
                                  renovatie_scenario: v === '' ? undefined : (v as RenovatieScenario),
                                });
                              }}
                              className="w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                            >
                              <option value="">Zelfde als standaard</option>
                              {(['Geen', 'Basis', 'Diep'] as RenovatieScenario[]).map((x) => (
                                <option key={x} value={x}>
                                  {x}
                                </option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Hoe strak is deze kamer?">
                            <select
                              value={r.luchtdichtheid ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRoom(r.id, {
                                  luchtdichtheid: v === '' ? undefined : (v as Luchtdichtheid),
                                });
                              }}
                              className="w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text"
                            >
                              <option value="">Zelfde als standaard</option>
                              {(['Loos', 'Normaal', 'Strak'] as Luchtdichtheid[]).map((x) => (
                                <option key={x} value={x}>
                                  {x}
                                </option>
                              ))}
                            </select>
                          </Field>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addRoom}
                  className="mt-4 rounded-xl border border-ct-accent/50 px-4 py-2 text-sm font-semibold text-ct-accent hover:bg-ct-accent/10"
                >
                  + Kamer toevoegen
                </button>
                <StepNav
                  onBack={() => setStep(3)}
                  onNext={() => setStep(5)}
                  stepIndex={3}
                  totalSteps={7}
                  prevStepTitle={stepPills[2]}
                  nextStepTitle={stepPills[4]}
                />
              </StepCard>
            )}

            {step === 5 && (
              <StepCard
                title={
                  <>
                    <Zap className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>{scanCtx.situatieStepTitel}</span>
                  </>
                }
              >
                <p className="text-sm leading-relaxed text-ct-muted">{scanCtx.situatieIntro}</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Meterkast (groepen / ruimte)"
                    tip="De kast waar je zekeringen of automaten zitten. Een warmtepomp vraagt vaak zwaardere groepen dan een oude ketel."
                  >
                    <select
                      value={input.situatie.meterkast_status}
                      onChange={(e) =>
                        patchSituatie('meterkast_status', e.target.value as MeterkastStatus)
                      }
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      <option value="weet_niet">Weet ik niet</option>
                      <option value="ruimte_genoeg">Lijkt ruim / recent genoeg</option>
                      <option value="waarschijnlijk_te_klein">Waarschijnlijk aan de krappe kant</option>
                      <option value="recent_vernieuwd">Recent vernieuwd door elektricien</option>
                    </select>
                  </Field>
                  <Field
                    label="Water dichtbij warmtepomp / buitenunit?"
                    tip="Voor sommige buitenunits wil je af en toe spoelen of vullen; een kraan dichtbij scheelt leidingwerk."
                  >
                    <select
                      value={input.situatie.water_warmtepomp_dichtbij}
                      onChange={(e) =>
                        patchSituatie('water_warmtepomp_dichtbij', e.target.value as WaterWarmtepompDichtbij)
                      }
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      <option value="weet_niet">Weet ik niet</option>
                      <option value="ja_dichtbij">Ja, kraan of leiding dichtbij</option>
                      <option value="ver_weg">Nee, ver weg</option>
                      <option value="lastig">Lastig / onbekend</option>
                    </select>
                  </Field>
                  <Field label="Wat hangt er nu aan verwarming?" tip="Globaal is genoeg — geen technische details nodig.">
                    <select
                      value={input.situatie.bestaande_verwarming}
                      onChange={(e) =>
                        patchSituatie('bestaande_verwarming', e.target.value as BestaandeVerwarming)
                      }
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      <option value="weet_niet">Weet ik niet</option>
                      <option value="geen">Nog niets vast / leeg pand</option>
                      <option value="gas_cv">Gas-ketel (cv)</option>
                      <option value="stadswarmte">Warmte uit de straat (stadswarmte)</option>
                      <option value="elektrisch">Elektrisch (direct of nacht)</option>
                      <option value="warmtepomp">Al een warmtepomp</option>
                      <option value="anders">Iets anders</option>
                    </select>
                  </Field>
                  <Field
                    label={scanCtx.utiliteit ? 'Hoe wordt het pand gebruikt?' : 'Hoe wordt het huis gebruikt?'}
                    tip="Leeg bouwen is makkelijker om leidingen mee te trekken."
                  >
                    <select
                      value={input.situatie.pand_gebruik}
                      onChange={(e) => patchSituatie('pand_gebruik', e.target.value as PandGebruik)}
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      {pandGebruikKeuzes(scanCtx.utiliteit).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field
                      label="Hoe groot is de klus?"
                      tip="Alleen om te zien of we rekening houden met alles tegelijk vernieuwen of alleen de verwarming."
                    >
                      <select
                        value={input.situatie.klus_omvang}
                        onChange={(e) => patchSituatie('klus_omvang', e.target.value as KlusOmvang)}
                        className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                      >
                        <option value="alleen_verwarmen_vernieuwen">Vooral verwarming vernieuwen</option>
                        <option value="binnenwerk">Ook flink binnenwerk</option>
                        <option value="flinke_renovatie">Grote renovatie</option>
                        <option value="sloop_en_opbouw">Sloop en nieuw</option>
                      </select>
                    </Field>
                  </div>
                </div>
                <StepNav
                  onBack={() => setStep(4)}
                  onNext={() => setStep(6)}
                  stepIndex={4}
                  totalSteps={7}
                  prevStepTitle={stepPills[3]}
                  nextStepTitle={stepPills[5]}
                />
              </StepCard>
            )}

            {step === 6 && (
              <StepCard
                title={
                  <>
                    <Sparkles className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>Wat wil je het liefst? (mag ook: geen voorkeur)</span>
                  </>
                }
              >
                <p className="text-sm leading-relaxed text-ct-muted">{scanCtx.wensenIntro}</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <Field label="Hoofdvoorkeur warmte" tip="Geen verplichting — alleen richting voor het advies.">
                    <select
                      value={input.wensen.warmte_voorkeur}
                      onChange={(e) => patchWensen('warmte_voorkeur', e.target.value as WarmteVoorkeur)}
                      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
                    >
                      <option value="geen_voorkeur">Geen voorkeur / eerst uitslag zien</option>
                      <option value="liever_warmtepomp">Liever warmtepomp</option>
                      <option value="liever_gas">Liever (blijven) op gas</option>
                      <option value="liever_hybride">Liever hybride (gas + pomp)</option>
                      <option value="liever_stadswarmte">Liever warmtenet (als het kan)</option>
                      <option value="geen_gas_meer">Liever helemaal geen gas meer</option>
                    </select>
                  </Field>
                  <div className="flex flex-col gap-4 sm:col-span-2">
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-ct-border bg-ct-elevated/50 px-4 py-3 text-sm text-ct-text">
                      <input
                        type="checkbox"
                        checked={input.wensen.wil_vloerverwarming_indien_kan}
                        onChange={(e) => patchWensen('wil_vloerverwarming_indien_kan', e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-semibold">Vloerverwarming</span> als het kan — ik hou van warme voeten
                        <HelpTip text="Vloerverwarming werkt het prettigst als de woning niet extreem veel warmte per vierkante meter vraagt. Bij zware kamers combineren we het vaak met iets anders." />
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-ct-border bg-ct-elevated/50 px-4 py-3 text-sm text-ct-text">
                      <input
                        type="checkbox"
                        checked={input.wensen.wil_geen_buitenunit_appartement}
                        onChange={(e) => patchWensen('wil_geen_buitenunit_appartement', e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-semibold">Geen kastje buiten</span> (bijv. appartement)
                        <HelpTip text="Zonder buitenunit zijn er minder standaard opties. Vaak kijk je dan naar binnen-units of afspraken met de VvE." />
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-ct-border bg-ct-elevated/50 px-4 py-3 text-sm text-ct-text">
                      <input
                        type="checkbox"
                        checked={input.wensen.wil_actieve_koeling}
                        onChange={(e) => patchWensen('wil_actieve_koeling', e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-semibold">Actieve koeling</span> (airco-achtig) is wenselijk
                        <HelpTip text="Actief koelen betekent: apparaat dat kou levert, niet alleen zonwering of raam open." />
                      </span>
                    </label>
                  </div>
                </div>
                <StepNav
                  onBack={() => setStep(5)}
                  onNext={() => setStep(7)}
                  stepIndex={5}
                  totalSteps={7}
                  prevStepTitle={stepPills[4]}
                  nextStepTitle={stepPills[6]}
                />
              </StepCard>
            )}

            {step === 7 && (
              <StepCard
                title={
                  <>
                    <ClipboardCheck className="h-6 w-6 text-ct-accent" aria-hidden />
                    <span>Klaar om je uitslag te maken</span>
                  </>
                }
              >
                <p className="text-sm text-ct-muted">
                  We controleren je antwoorden en maken de cijfers. Daarna zie je in <strong className="text-ct-text">
                    gewone taal
                  </strong>{' '}
                  wat warmte en koeling hier betekenen, wat per kamer speelt, en welke vervolgstappen logisch zijn.
                </p>
                <StepNav
                  onBack={() => setStep(6)}
                  onNext={() => void runEngine()}
                  stepIndex={6}
                  totalSteps={7}
                  prevStepTitle={stepPills[5]}
                  nextLabel="Resultaat maken"
                />
              </StepCard>
            )}
          </>
        )}

        {step === RESULT_STEP && result && (
          <div className="space-y-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-ct-text">Resultaat</h2>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/rapport"
                  className="rounded-full border border-ct-accent/60 bg-ct-accent/10 px-4 py-2 text-sm font-semibold text-ct-accent hover:bg-ct-accent/20"
                >
                  Print-rapport (vol scherm)
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setResult(null);
                    setReportMarkdown(null);
                    setReportError(null);
                  }}
                  className="text-sm font-medium text-ct-muted hover:text-ct-accent hover:underline"
                >
                  ← Nieuwe invoer
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {result.scenarios.map((s, si) => (
                <div
                  key={s.id}
                  className={`rounded-2xl border p-5 transition hover:border-ct-accent/30 ${
                    s.id === 'base'
                      ? 'border-ct-accent bg-ct-accent/10'
                      : 'border-ct-border bg-ct-surface'
                  }`}
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-ct-muted">{s.label}</p>
                  <p className="mt-1 text-sm text-ct-muted">{s.subtitle}</p>
                  <p className="mt-4 flex items-baseline gap-2 text-3xl font-bold text-ct-text">
                    {s.pieklastKW} kW
                    <HelpTip text="Het zwaarste moment op een koude winterdag of warme zomerdag — niet je jaarfactuur." />
                  </p>
                  <p className="text-xs text-ct-muted">Zwaarste moment: verwarmen óf koelen</p>
                  <div className="mt-3 flex gap-4 text-sm">
                    <span className="text-ct-muted">
                      Verwarmen: <strong className="text-ct-text">{s.warmtelastKW}</strong> kW
                    </span>
                    <span className="text-ct-muted">
                      Koelen: <strong className="text-ct-text">{s.koellastKW}</strong> kW
                    </span>
                  </div>
                  <div className="mt-4">
                    <AnimatedBar
                      ratio={s.pieklastKW / maxScenarioPiek}
                      className="bg-gradient-to-r from-ct-accent to-sky-400"
                      delayMs={si * 90}
                      ariaLabel={`Scenario ${s.label}: ${s.pieklastKW} kilowatt piek`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="text-lg font-bold text-ct-text">Scenario-vergelijking (tabel)</h3>
              <p className="mt-2 text-sm text-ct-muted">
                Zelfde drie scenario&apos;s als de kaarten hierboven — handig om naast elkaar te zien.
              </p>
              <div className="mt-4 overflow-x-auto rounded-xl border border-ct-border">
                <table className="w-full min-w-[480px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-ct-border bg-ct-bg/80 text-ct-muted">
                      <th className="px-3 py-2">Scenario</th>
                      <th className="px-3 py-2">Piek kW</th>
                      <th className="px-3 py-2">Warmte kW</th>
                      <th className="px-3 py-2">Koel kW</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.scenarios.map((s) => (
                      <tr key={s.id} className="border-b border-ct-border/50">
                        <td className="px-3 py-2 font-medium text-ct-text">{s.label}</td>
                        <td className="px-3 py-2 tabular-nums text-ct-text">{s.pieklastKW}</td>
                        <td className="px-3 py-2 tabular-nums text-ct-muted">{s.warmtelastKW}</td>
                        <td className="px-3 py-2 tabular-nums text-ct-muted">{s.koellastKW}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="text-lg font-bold text-ct-text">Overzicht downloaden (gratis)</h3>
              <p className="mt-2 text-sm text-ct-muted">
                Een eenvoudig tabelbestand met jouw antwoorden en de uitkomsten die je hier ziet — handig in Excel of
                als bijlage bij een offerteaanvraag. Geen verborgen technische details van onze berekening.
              </p>
              <button
                type="button"
                onClick={() => downloadComfortSummaryCsv(input, result)}
                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-ct-border bg-ct-elevated px-4 py-2.5 text-sm font-semibold text-ct-text hover:bg-ct-bg"
              >
                <FileDown className="h-4 w-4" aria-hidden />
                Download overzicht (tabel)
              </button>
            </section>

            <section className="rounded-2xl border border-dashed border-ct-border bg-ct-surface/60 p-5">
              <h3 className="text-sm font-bold uppercase tracking-wide text-ct-accent">Wetten &amp; regels (kort)</h3>
              <p className="mt-2 text-sm leading-relaxed text-ct-muted">{result.segmentRegelgevingKort}</p>
            </section>

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="text-lg font-bold text-ct-text">Per ruimte — in gewone taal</h3>
              <p className="mt-2 text-sm leading-relaxed text-ct-muted">{result.jipEnJannekeSamenvatting}</p>
              <p className="mt-3 text-xs leading-relaxed text-ct-muted">
                <strong className="text-ct-text">Snelle uitleg van de getallen hieronder:</strong> watt per vierkante
                meter zegt hoe “zwaar” een ruimte is om warm te houden; watt per kubieke meter kijkt ook naar de hoogte
                van de ruimte.{' '}
                <HelpTip text="W/m² = vermogen gedeeld door vloeroppervlak. W/m³ = hetzelfde vermogen gedeeld door de inhoud (lucht in de ruimte). Hoger = meer vraag naar verwarming of koeling." />
              </p>
              <p className="mt-2 text-xs text-ct-muted">
                Heel gebouw samen: <strong className="text-ct-text">{result.gebouwIntensiteit.warmte_W_m2} W/m²</strong>{' '}
                voor warmte · <strong className="text-ct-text">{result.gebouwIntensiteit.koel_W_m2} W/m²</strong> voor
                koel · inhoud meegerekend:{' '}
                <strong className="text-ct-text">{result.gebouwIntensiteit.warmte_W_m3} W/m³</strong> warm /{' '}
                <strong className="text-ct-text">{result.gebouwIntensiteit.koel_W_m3} W/m³</strong> koel
              </p>
              <div className="mt-4 overflow-x-auto rounded-xl border border-ct-border">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-ct-border bg-ct-bg/80 text-ct-muted">
                      <th className="px-3 py-2">Ruimte</th>
                      <th className="px-3 py-2">Verd.</th>
                      <th className="px-3 py-2">m²</th>
                      <th className="px-3 py-2">Warmte W</th>
                      <th className="px-3 py-2">W/m²</th>
                      <th className="px-3 py-2">Koel W</th>
                      <th className="px-3 py-2">Jaar kWh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.ruimteLasten.map((row) => (
                      <tr key={row.id} className="border-b border-ct-border/50">
                        <td className="px-3 py-2 text-ct-text">
                          <div className="font-medium">{row.naam}</div>
                          <div className="mt-0.5 text-[11px] leading-snug text-ct-muted">
                            Warmte: {Math.round(row.warmte_transmissie_W)} W via muren en ramen,{' '}
                            {Math.round(row.warmte_ventilatie_W)} W via lucht. Koeling: {Math.round(row.koel_transmissie_W)}{' '}
                            W muren/ramen, {Math.round(row.koel_ventilatie_W)} W lucht, {Math.round(row.koel_zon_W)} W
                            zon op het glas.
                          </div>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-ct-muted">{row.verdieping}</td>
                        <td className="px-3 py-2 tabular-nums text-ct-muted">{row.vloeroppervlakte_m2}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-ct-text">
                          {row.warmte_W.toLocaleString('nl-NL')}
                        </td>
                        <td className="px-3 py-2 font-mono tabular-nums text-ct-muted">{row.warmte_W_m2}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-ct-text">
                          {row.koel_W.toLocaleString('nl-NL')}
                        </td>
                        <td className="px-3 py-2 text-xs text-ct-muted">
                          {row.jaar_warmte_kWh + row.jaar_koel_kWh} ({row.jaar_warmte_kWh} w / {row.jaar_koel_kWh} k)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Link
                to="/rapport"
                className="mt-4 inline-flex text-sm font-semibold text-ct-accent hover:underline"
              >
                → Uitgebreid rapport met grafieken, advies en woordenlijst
              </Link>
            </section>

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="text-lg font-bold text-ct-text">Installatie-opties (indicatief)</h3>
              <p className="mt-2 text-sm text-ct-muted">
                Vaste regels op basis van je lasten — geen vervanging voor een installateur of projectontwerp.
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                {(result.adviesTechnieken ?? []).map((a, i) => (
                  <li
                    key={`${a.categorie}-${i}`}
                    className="rounded-xl border border-ct-border/80 bg-ct-elevated/40 px-4 py-3"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-ct-accent">
                      {ADVIES_CAT_NL[a.categorie] ?? a.categorie}
                    </p>
                    <p className="mt-1 font-semibold text-ct-text">{a.titel}</p>
                    <p className="mt-1 leading-relaxed text-ct-muted">{a.toelichting}</p>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-2xl border border-ct-accent/25 bg-gradient-to-br from-ct-surface to-ct-bg p-6">
              <h3 className="text-lg font-bold text-ct-text">Meer detail (als je verder wilt lezen)</h3>
              <p className="mt-1 text-xs text-ct-muted">
                Ingevuld als:{' '}
                <strong className="text-ct-text">{USER_SEGMENT_LABELS[result.userSegment]}</strong> · spreiding tussen
                scenario’s: <strong className="text-ct-text">{result.engineInsights.onzekerheidLabel}</strong>
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ct-muted">{result.engineInsights.interpretatieRegel}</p>
              <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-xl border border-ct-border/80 bg-ct-elevated/50 px-4 py-3">
                  <dt className="text-xs uppercase tracking-wide text-ct-muted">Jaarverdeling</dt>
                  <dd className="mt-1 font-semibold text-ct-text">
                    {result.engineInsights.warmteAandeelJaarPct}% warmte ·{' '}
                    {result.engineInsights.koelAandeelJaarPct}% koel
                  </dd>
                  <dd className="mt-1 text-xs text-ct-muted">
                    {result.jaarverbruikWarmteKWh.toLocaleString('nl-NL')} /{' '}
                    {result.jaarverbruikKoelKWh.toLocaleString('nl-NL')} kWh
                  </dd>
                </div>
                <div className="rounded-xl border border-ct-border/80 bg-ct-elevated/50 px-4 py-3">
                  <dt className="text-xs uppercase tracking-wide text-ct-muted">Verbruik per m² vloer (indicatie)</dt>
                  <dd className="mt-1 font-semibold text-ct-text">
                    {result.engineInsights.energieIntensiteitKWhPerM2Jaar} kWh/m²·jr
                  </dd>
                  <dd className="mt-1 text-xs text-ct-muted">
                    Stroom voor warmtepomp (ruwe bandbreedte): {result.engineInsights.elektraIntensiteitKWhPerM2JaarMin} –{' '}
                    {result.engineInsights.elektraIntensiteitKWhPerM2JaarMax} kWh/m²·jr
                  </dd>
                </div>
                <div className="rounded-xl border border-ct-border/80 bg-ct-elevated/50 px-4 py-3">
                  <dt className="text-xs uppercase tracking-wide text-ct-muted">Verlies via muren/ramen vs. lucht (winter)</dt>
                  <dd className="mt-1 font-semibold text-ct-text">
                    {result.engineInsights.transmissieAandeelBrutoWarmtePct}% via muren en ramen
                  </dd>
                </div>
                <div className="rounded-xl border border-ct-border/80 bg-ct-elevated/50 px-4 py-3">
                  <dt className="text-xs uppercase tracking-wide text-ct-muted">Zon op ramen bij koelpieken</dt>
                  <dd className="mt-1 font-semibold text-ct-text">
                    {result.engineInsights.zonAandeelKoelBrutoPct}% van de koel-last
                  </dd>
                  <dd className="mt-1 text-xs text-ct-muted">
                    Verschil tussen scenario’s (piek): ±{result.engineInsights.scenarioPiekBandPct}%
                  </dd>
                </div>
              </dl>
            </section>

            {(climateLoading || result.climateContext) && (
              <section className="rounded-2xl border border-ct-border border-dashed bg-ct-surface/50 p-6">
                <h3 className="text-lg font-bold text-ct-text">Weer in jouw streek (voor de jaarschatting)</h3>
                {climateLoading && <p className="mt-2 text-sm text-ct-muted">Weergegevens laden…</p>}
                {result.climateContext && !climateLoading && (
                  <div className="mt-3 space-y-2 text-sm text-ct-muted">
                    <p>
                      <strong className="text-ct-text">{result.climateContext.locationLabel}</strong> — periode{' '}
                      {result.climateContext.period}. Jaargemiddelde temperatuur: {result.climateContext.meanTempYear} °C.
                    </p>
                    <p>
                      Koude-graaddagen (18 °C): <strong className="text-ct-text">{result.climateContext.hdd18}</strong> —
                      warmte-graaddagen (24 °C):{' '}
                      <strong className="text-ct-text">{result.climateContext.cdd24}</strong>.
                    </p>
                    <p>
                      Vergeleken met een NL-referentie (HDD {result.climateContext.refHdd} / CDD{' '}
                      {result.climateContext.refCdd}): verwarmen × {result.climateContext.factorHeat}, koelen ×{' '}
                      {result.climateContext.factorCool}.
                    </p>
                    <p className="text-xs">
                      <a
                        href={result.climateContext.attribution}
                        target="_blank"
                        rel="noreferrer"
                        className="text-ct-accent underline"
                      >
                        Bron weergegevens
                      </a>
                      . {result.climateContext.note}
                    </p>
                  </div>
                )}
              </section>
            )}

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="text-lg font-bold text-ct-text">Waar de hoofdcijfers vandaan komen</h3>
              <p className="mt-2 text-sm text-ct-muted">
                Korte technische toelichting (handig om door te vragen bij een adviseur): {result.methodologyNote}
              </p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-ct-border text-ct-muted">
                      <th className="py-2 pr-4">Post</th>
                      <th className="py-2">Watt</th>
                      <th className="py-2">Soort</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.lineItemsBase.map((row) => (
                      <tr key={row.key} className="border-b border-ct-border/60">
                        <td className="py-2 pr-4 text-ct-text">{row.label}</td>
                        <td className="py-2 font-mono tabular-nums text-ct-text">
                          {Math.round(row.valueW).toLocaleString('nl-NL')}
                        </td>
                        <td className="py-2 capitalize text-ct-muted">
                          {row.category} · {row.heatOrCool}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs text-ct-muted">
                Ontwerp: binnen {result.designConditions.tIndoorC} °C, buiten winter{' '}
                {result.designConditions.tOutdoorWinterC} °C / zomer {result.designConditions.tOutdoorSummerC} °C —{' '}
                {result.designConditions.note}
              </p>
            </section>

            <section className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-ct-border bg-ct-elevated p-6">
                <h3 className="font-bold text-ct-text">Capaciteitsadvies</h3>
                <p className="mt-3 text-sm leading-relaxed text-ct-muted">
                  Richtcapaciteit: <strong className="text-ct-text">{result.pieklastKWDisplay} kW</strong>
                </p>
                <p className="mt-4 text-sm text-ct-text">{result.systeem}</p>
              </div>
              <div className="rounded-2xl border border-ct-border bg-ct-elevated p-6">
                <h3 className="font-bold text-ct-text">Alternatief spoor</h3>
                <p className="mt-4 text-sm text-ct-muted">{result.alternatief}</p>
              </div>
            </section>

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="font-bold text-ct-text">Jaarlasten (indicatief)</h3>
              <p className="mt-2 text-sm text-ct-muted">
                Thermische jaarindicatie (geen uurlijkse simulatie):{' '}
                <strong className="text-ct-text">{result.jaarverbruik.toLocaleString('nl-NL')} kWh</strong> totaal
                geschat.
              </p>
              <p className="mt-3 text-sm text-ct-muted">
                Elektra-hypothese WP: {result.jaarverbruikElektraHypotheseKWh.min.toLocaleString('nl-NL')} –{' '}
                {result.jaarverbruikElektraHypotheseKWh.max.toLocaleString('nl-NL')} kWh/jaar.{' '}
                {result.jaarverbruikElektraHypotheseKWh.note}
              </p>
            </section>

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="font-bold text-ct-text">Jaartotalen per uur (extra check)</h3>
              <p className="mt-2 text-sm leading-relaxed text-ct-muted">
                Zelfde huis als hierboven, maar nu met <strong className="text-ct-text">temperatuur per uur</strong> uit
                het weerarchief ({hourlySim?.openMeteoHourly?.startDate?.slice(0, 4) ?? 'vorig kalenderjaar'}, rond je
                postcode). Je krijgt <strong className="text-ct-text">twee varianten</strong>: één zonder traagheid in
                het gebouw, één mét “zwaarte” van muren/vloer (kies hieronder). Vergelijk beide met de jaarschatting
                hierboven — drie manieren om hetzelfde huis te bekijken.
              </p>
              <label className="mt-4 block text-sm font-medium text-ct-muted">Hoe zwaar voelt je huis aan? (voor de tweede variant)</label>
              <select
                value={thermalInertia}
                onChange={(e) => setThermalInertia(e.target.value as ThermalInertiaOption)}
                className="mt-1.5 w-full max-w-md rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-sm text-ct-text"
              >
                {INERTIA_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-ct-muted">
                Een andere keuze wist het vorige uur-resultaat tot je opnieuw ophaalt.
              </p>
              <button
                type="button"
                onClick={() => void loadHourlyThermal()}
                disabled={hourlySimLoading || climateLoading}
                className="mt-4 rounded-xl border border-ct-accent/50 bg-ct-accent/10 px-5 py-2.5 text-sm font-bold text-ct-accent hover:bg-ct-accent/20 disabled:opacity-50"
              >
                {hourlySimLoading ? 'Bezig met rekenen…' : 'Haal jaartotalen per uur op'}
              </button>
              {hourlySimError && <p className="mt-3 text-sm text-red-400">{hourlySimError}</p>}
              {hourlySim && (
                <div className="mt-4 space-y-4 rounded-xl border border-ct-border/80 bg-ct-elevated/50 p-4 text-sm">
                  <p className="text-ct-muted">
                    <span className="font-semibold text-ct-text">{hourlySim.openMeteoHourly.hours}</span> uren ·{' '}
                    {hourlySim.openMeteoHourly.locationLabel}
                  </p>
                  {(() => {
                    const maxHeat = Math.max(
                      hourlySim.hourlyThermal.heating_kWh,
                      hourlySim.hourlyThermalRc1.heating_kWh,
                      1
                    );
                    return (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-ct-border/60 bg-ct-bg/60 p-3">
                      <p className="text-xs font-bold uppercase text-ct-accent">Snelle rekensom per uur</p>
                      <p className="mt-2 text-ct-muted">
                        Verwarmen{' '}
                        <strong className="text-ct-text">
                          {hourlySim.hourlyThermal.heating_kWh.toLocaleString('nl-NL')}
                        </strong>{' '}
                        kWh · Koelen{' '}
                        <strong className="text-ct-text">
                          {hourlySim.hourlyThermal.cooling_kWh.toLocaleString('nl-NL')}
                        </strong>{' '}
                        kWh
                      </p>
                      <div className="mt-3">
                        <AnimatedBar
                          ratio={hourlySim.hourlyThermal.heating_kWh / maxHeat}
                          className="bg-ct-warn/90"
                          delayMs={40}
                          ariaLabel="Verwarmen variant snel"
                        />
                      </div>
                    </div>
                    <div className="rounded-lg border border-ct-border/60 bg-ct-bg/60 p-3">
                      <p className="text-xs font-bold uppercase text-ct-accent">Met gebouw-massa meegerekend</p>
                      <p className="mt-2 text-ct-muted">
                        Verwarmen{' '}
                        <strong className="text-ct-text">
                          {hourlySim.hourlyThermalRc1.heating_kWh.toLocaleString('nl-NL')}
                        </strong>{' '}
                        kWh · Koelen{' '}
                        <strong className="text-ct-text">
                          {hourlySim.hourlyThermalRc1.cooling_kWh.toLocaleString('nl-NL')}
                        </strong>{' '}
                        kWh
                      </p>
                      <div className="mt-3">
                        <AnimatedBar
                          ratio={hourlySim.hourlyThermalRc1.heating_kWh / maxHeat}
                          className="bg-sky-500/90"
                          delayMs={120}
                          ariaLabel="Verwarmen variant met massa"
                        />
                      </div>
                      <p className="mt-2 text-xs text-ct-muted">
                        Geschatte warmtebuffer: ca. {hourlySim.thermalMeta.heatCapacity_WhPerK.toLocaleString('nl-NL')}{' '}
                        wattuur per graad verschil · gemiddeld verlies door de schil ca.{' '}
                        {Math.round(hourlySim.thermalMeta.uaEnv_WperK)} watt per graad verschil met buiten
                      </p>
                    </div>
                  </div>
                    );
                  })()}
                  <p className="text-xs text-ct-muted">
                    Binnen {hourlySim.coeffs.tIndoorC} °C als streeftemperatuur; koelen actief boven 24 °C in deze
                    vereenvoudigde rekensom.
                  </p>
                  <p className="text-xs text-ct-muted">{hourlySim.openMeteoHourly.note}</p>
                </div>
              )}
            </section>

            <ResultPurchaseSection
              input={input}
              prijsSnapshot={prijsSnapshot}
              bundleTier={bundleTier}
              setBundleTier={setBundleTier}
              woonCheckoutOk={woonCheckoutOk}
              payEmail={payEmail}
              setPayEmail={setPayEmail}
              checkoutLoading={checkoutLoading}
              checkoutError={checkoutError}
              onCheckout={() => void goToStripe()}
              indicativePricingParagraph={indicativePricingParagraph()}
              selectedAddons={checkoutAddons}
              setSelectedAddons={setCheckoutAddons}
            />

            <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
              <h3 className="font-bold text-ct-text">Uitgeschreven adviesrapport</h3>
              <div className="mt-3 rounded-lg ct-caution-panel px-3 py-2 text-xs text-ct-muted">
                <strong className="ct-caution-heading">Let op:</strong> het rapport is een leesbare samenvatting op basis
                van jouw invoer — geen officieel document en geen vervanging voor een adviseur ter plaatse.
              </div>
              <p className="mt-3 text-sm leading-relaxed text-ct-muted">
                <strong className="text-ct-text">Richtprijs:</strong>{' '}
                {indicativeBundlePriceLineForInput({
                  user_segment: input.user_segment,
                  vloeroppervlakte: input.vloeroppervlakte,
                  type_woning: input.type_woning,
                  bundleTier,
                })}
                .
                {prijsSnapshot.requiresQuote
                  ? ' Voor dit oppervlak volgt maatwerk via offerte i.p.v. vaste webprijs.'
                  : ' Definitief bedrag zie je bij afrekenen.'}
              </p>
              <p className="mt-2 text-sm text-ct-muted">
                <strong className="text-ct-text">Comfort Basis:</strong> een vaste, duidelijke tekst op basis van jouw
                invoer en de cijfers op dit scherm — zonder extra “schrijf-laag”.{' '}
                <strong className="text-ct-text">Comfort Plus:</strong> een uitgebreider leesbaar rapport, wanneer die
                optie voor jouw omgeving beschikbaar is.
              </p>
              {bundleTier === 'plus' && !reportAvailable ? (
                <p className="mt-3 text-sm text-ct-muted">
                  Het uitgebreide Plus-rapport is hier nog niet beschikbaar. Kies hierboven{' '}
                  <strong className="text-ct-text">Comfort Basis</strong> voor een directe samenvatting, of neem contact
                  op met ons als je Plus nodig hebt.
                </p>
              ) : (
                <>
                  {bundleTier === 'plus' && (
                    <label className="mt-4 block text-sm text-ct-muted">
                      <span className="font-medium text-ct-text">Optioneel — naamregel op het rapport</span>
                      <span className="mt-1 block text-xs text-ct-muted/90">
                        Bijv. &quot;Namens [bedrijfsnaam]&quot; — één korte regel; leeg laten mag.
                      </span>
                      <input
                        type="text"
                        value={reportBrandingLine}
                        onChange={(e) => setReportBrandingLine(e.target.value)}
                        maxLength={120}
                        placeholder="Bijv. Namens Installatiebedrijf X"
                        className="mt-2 w-full max-w-md rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-sm text-ct-text placeholder:text-ct-muted/50"
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => void loadFullReport()}
                    disabled={reportLoading}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-ct-accent px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" aria-hidden />
                    {reportLoading
                      ? 'Rapport schrijven…'
                      : bundleTier === 'basis'
                        ? 'Maak samenvatting (Basis)'
                        : 'Maak mijn adviesrapport (Plus)'}
                  </button>
                  {reportError && <p className="mt-3 text-sm text-red-400">{reportError}</p>}
                  {reportMarkdown && (
                    <div className="mt-6 max-h-[70vh] overflow-y-auto rounded-xl border border-ct-border bg-ct-bg p-5">
                      <SimpleMarkdown text={reportMarkdown} />
                      {reportServerDisclaimer && (
                        <p
                          className="mt-4 border-t border-ct-border pt-4 text-xs leading-relaxed text-ct-muted"
                          role="note"
                        >
                          {reportServerDisclaimer}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>

            <p className="text-xs leading-relaxed text-ct-muted/80">{result.disclaimer}</p>
          </div>
        )}
      </div>
    </>
  );
}

function StepCard({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="ct-step-enter rounded-2xl border border-ct-border bg-ct-surface p-6 shadow-xl shadow-black/10">
      <h2 className="flex flex-wrap items-center gap-2 text-xl font-bold text-ct-text">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function StepNav({
  onBack,
  onNext,
  nextLabel = 'Volgende stap',
  stepIndex,
  totalSteps,
  prevStepTitle,
  nextStepTitle,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  stepIndex?: number;
  totalSteps?: number;
  prevStepTitle?: string;
  nextStepTitle?: string;
}) {
  return (
    <div className="mt-8 space-y-4">
      {stepIndex != null && totalSteps != null && (
        <p className="text-center text-sm text-ct-muted">
          Stap <span className="font-semibold text-ct-text">{stepIndex + 1}</span> van{' '}
          <span className="font-semibold text-ct-text">{totalSteps}</span>
        </p>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:gap-6">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border border-ct-border px-5 py-3 text-left transition hover:bg-ct-elevated"
        >
          <span className="text-sm font-bold text-ct-text">← Vorige</span>
          {prevStepTitle ? (
            <span className="mt-0.5 block text-xs text-ct-muted">Terug naar: {prevStepTitle}</span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-xl bg-ct-accent px-5 py-3 text-right font-bold text-white transition hover:bg-blue-500"
        >
          <span className="text-sm">{nextLabel}</span>
          {nextStepTitle ? (
            <span className="mt-0.5 block text-xs font-normal text-white/90">Daarna: {nextStepTitle} →</span>
          ) : null}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  tip,
  children,
}: {
  label: string;
  tip?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="flex items-center gap-0.5 text-sm font-medium text-ct-muted">
        {label}
        {tip ? <HelpTip text={tip} /> : null}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Num({
  value,
  onChange,
  step = 1,
  min = 0,
  max,
}: {
  value: number;
  onChange: (n: number) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value || ''}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
    />
  );
}

function Select<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: T[];
  onChange: (v: T) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="w-full rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
